import React, { Component } from 'react';
import { Col, Row, Form, Button, Tab, Nav } from 'react-bootstrap';
import { Fetcher } from '../../Helpers/fetcher.js';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './index.css';

const { SearchBar } = Search;

/* ── helpers ── */
const barColor = (pct) => {
    if (pct >= 80) return '#e74a3b';
    if (pct >= 50) return '#f6a623';
    return '#2ecc71';
};

const getMetricSeverity = (server) => {
    const status = (server && server.status) ? String(server.status).toLowerCase().trim() : '';
    if (!server || status !== 'online') return 'offline';
    const memTotal  = parseFloat(server.memTotal)  || 0;
    const memUsed   = parseFloat(server.memUsed)   || 0;
    const diskTotal = parseFloat(server.diskTotal) || 0;
    const diskUsed  = parseFloat(server.diskUsed)  || 0;
    const cpuPct    = parseFloat(server.cpuUsed)   || 0;
    const cores     = (parseFloat(server.cpuCores) || 0) > 0 ? parseFloat(server.cpuCores) : 1;
    const loadPct   = ((parseFloat(server.load1m) || 0) / cores) * 100;
    const memPct    = memTotal  > 0 ? (memUsed  / memTotal)  * 100 : 0;
    const diskPct   = diskTotal > 0 ? (diskUsed / diskTotal) * 100 : 0;
    if (cpuPct >= 80 || memPct >= 80 || diskPct >= 80 || loadPct >= 80) return 'critical';
    if (cpuPct >= 50 || memPct >= 50 || diskPct >= 50 || loadPct >= 50) return 'warning';
    return 'online';
};

const getGroup = (serverName) => {
    if (!serverName) return 'db';
    const n = serverName.toUpperCase();
    if (n.includes('_AHM')) return 'ahm';
    if (n.includes('_BLR')) return 'blr';
    return 'db';
};

const GROUP_META = {
    db:  { label: 'DB & Infrastructure' },
    ahm: { label: 'AHM Dialers' },
    blr: { label: 'BLR Dialers' },
};

const SEVERITY_ORDER = { critical: 0, warning: 1, offline: 2, online: 3 };

const dotColorBySeverity = (severity) => {
    if (severity === 'critical') return '#e74a3b';
    if (severity === 'warning')  return '#f6a623';
    if (severity === 'offline')  return '#ccc';
    return '#2ecc71';
};

const MetricRow = ({ label, pct, pctLabel, subValue }) => (
    <div className="mc-metric-row">
        <div className="mc-metric-top">
            <span className="mc-metric-label">{label}</span>
            <div className="mc-bar-wrap">
                <div className="mc-bar" style={{ width: `${pct}%`, background: barColor(pct) }} />
            </div>
            <span className="mc-metric-pct" style={{ color: barColor(pct) }}>{pctLabel}</span>
        </div>
        {subValue && <div className="mc-metric-sub">{subValue}</div>}
    </div>
);

class Monitoring extends Component {
    constructor(props) {
        super(props);

        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);

        this.state = {
            latestList: [],
            latestLoading: false,
            activeFilter: 'all',
            lastUpdated: null,

            historyServerName: '',
            historyFromDate: yesterday,
            historyToDate: now,
            historyList: [],
            historyLoading: false,
            serverNameList: [],

            allFromDate: yesterday,
            allToDate: now,
            allList: [],
            allLoading: false,

            activeTab: 'latest',
        };
    }

    componentDidMount() {
        this.loadLatest();
        // this.autoRefresh = setInterval(() => {
        //     if (document.visibilityState === 'visible' && !this.fetchInFlight) {
        //         this.loadLatest();
        //     }
        // }, 60000);
    }

    componentWillUnmount() {
        clearInterval(this.autoRefresh);
    }

    formatDateTime(date) {
        if (!date) return '';
        const p = n => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())} ${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
    }

    loadLatest() {
        if (this.fetchInFlight) return;
        this.fetchInFlight = true;
        const { apiPath } = this.props.app;
        this.setState({ latestLoading: true });
        Fetcher.get(apiPath + '/api/monitoring/servers/latest')
            .then(res => {
                this.fetchInFlight = false;
                if (res && !res.error) {
                    const serverNames = res.map(s => s.serverName).filter(Boolean);
                    const update = {
                        latestList: res,
                        latestLoading: false,
                        lastUpdated: new Date(),
                        serverNameList: serverNames,
                    };
                    if (!this.state.historyServerName) {
                        update.historyServerName = serverNames[0] || '';
                    }
                    this.setState(update);
                } else {
                    this.setState({ latestList: [], latestLoading: false });
                }
            })
            .catch(() => {
                this.fetchInFlight = false;
                this.setState({ latestLoading: false });
            });
    }

    loadHistory() {
        const { apiPath } = this.props.app;
        const { historyServerName, historyFromDate, historyToDate } = this.state;
        if (!historyServerName) return;
        this.setState({ historyLoading: true });
        const from = this.formatDateTime(historyFromDate);
        const to   = this.formatDateTime(historyToDate);
        Fetcher.get(
            apiPath + `/api/monitoring/servers/history?serverName=${encodeURIComponent(historyServerName)}&fromDate=${encodeURIComponent(from)}&toDate=${encodeURIComponent(to)}`
        )
            .then(res => this.setState({ historyList: res && !res.error ? res : [], historyLoading: false }))
            .catch(() => this.setState({ historyLoading: false }));
    }

    loadAllServers() {
        const { apiPath } = this.props.app;
        const { allFromDate, allToDate } = this.state;
        this.setState({ allLoading: true });
        const from = this.formatDateTime(allFromDate);
        const to   = this.formatDateTime(allToDate);
        Fetcher.get(
            apiPath + `/api/monitoring/servers/all?fromDate=${encodeURIComponent(from)}&toDate=${encodeURIComponent(to)}`
        )
            .then(res => this.setState({ allList: res && !res.error ? res : [], allLoading: false }))
            .catch(() => this.setState({ allLoading: false }));
    }

    /* ── single card ── */
    renderCard(server, idx) {
        const severity = server._severity;
        const isOffline = severity === 'offline';

        const memPct   = server.memTotal  > 0 ? Math.min((server.memUsed  / server.memTotal)  * 100, 100) : 0;
        const diskPct  = server.diskTotal > 0 ? Math.min((server.diskUsed / server.diskTotal) * 100, 100) : 0;
        const cpuPct   = Math.min(server.cpuUsed || 0, 100);
        const cores    = server.cpuCores > 0 ? server.cpuCores : 1;
        const loadPct  = Math.min(((server.load1m || 0) / cores) * 100, 100);

        return (
            <div key={idx} className={`mc-card mc-card-${severity}`}>
                    <div className="mc-card-header">
                        <span className={`mc-server-name ${isOffline ? 'mc-name-offline' : ''}`}>
                            {server.serverName}
                        </span>
                        <span
                            className={`mc-status-dot ${severity === 'critical' ? 'mc-dot-blink' : ''}`}
                            style={{ color: dotColorBySeverity(severity) }}
                        >●</span>
                    </div>

                    {isOffline ? (
                        <div className="mc-offline-body">unreachable</div>
                    ) : (
                        <div className="mc-card-body">
                            <MetricRow label="CPU"  pct={cpuPct}  pctLabel={`${server.cpuUsed}%`} />
                            <MetricRow label="RAM"  pct={memPct}  pctLabel={`${memPct.toFixed(0)}%`}  subValue={`${server.memUsed} / ${server.memTotal} GB`} />
                            <MetricRow label="DISK" pct={diskPct} pctLabel={`${diskPct.toFixed(0)}%`} subValue={`${server.diskUsed} / ${server.diskTotal} GB`} />
                            <MetricRow label="LOAD" pct={loadPct} pctLabel={`${server.load1m}`}       subValue={`5m ${server.load5m}  15m ${server.load15m}  cores ${cores}`} />

                            <div className="mc-card-footer">
                                <span className="mc-footer-ip">{server.ip}</span>
                                {server._group !== 'db' && (
                                    <span className={`mc-calls-badge ${server.activeCalls > 0 ? 'mc-calls-active' : ''}`}>
                                        {server.activeCalls} calls
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
            </div>
        );
    }

    /* ── Latest Status Tab ── */
    renderLatestCards() {
        const { latestList, latestLoading, activeFilter } = this.state;

        if (latestLoading) return <p className="text-muted">Loading...</p>;
        if (!latestList || latestList.length === 0)
            return <p className="text-muted">No server data available.</p>;

        const servers = latestList.map(s => ({
            ...s,
            _severity: getMetricSeverity(s),
            _group: getGroup(s.serverName),
        }));

        const isOnline = s => (s.status || '').toLowerCase().trim() === 'online';

        const total    = servers.length;
        const online   = servers.filter(s => isOnline(s)).length;
        const warning  = servers.filter(s => s._severity === 'warning').length;
        const critical = servers.filter(s => s._severity === 'critical').length;
        const offline  = servers.filter(s => !isOnline(s)).length;

        let filtered = servers;
        if      (activeFilter === 'critical') filtered = servers.filter(s => s._severity === 'critical');
        else if (activeFilter === 'warning')  filtered = servers.filter(s => s._severity === 'warning');
        else if (activeFilter === 'online')   filtered = servers.filter(s => isOnline(s));
        else if (activeFilter === 'offline')  filtered = servers.filter(s => !isOnline(s));
        else if (activeFilter === 'db')       filtered = servers.filter(s => s._group === 'db');
        else if (activeFilter === 'ahm')      filtered = servers.filter(s => s._group === 'ahm');
        else if (activeFilter === 'blr')      filtered = servers.filter(s => s._group === 'blr');

        const groups = ['db', 'ahm', 'blr']
            .map(g => ({
                key: g,
                label: GROUP_META[g].label,
                servers: filtered
                    .filter(s => s._group === g)
                    .sort((a, b) => (a.serverName || '').localeCompare(b.serverName || '', undefined, { numeric: true, sensitivity: 'base' })),
            }))
            .filter(g => g.servers.length > 0);

        const FILTERS = [
            { key: 'all',      label: 'All' },
            { key: 'critical', label: 'Critical' },
            { key: 'warning',  label: 'Warning' },
            { key: 'online',   label: 'Online' },
            { key: 'offline',  label: 'Offline' },
            { key: 'db',       label: 'DB' },
            { key: 'ahm',      label: 'AHM' },
            { key: 'blr',      label: 'BLR' },
        ];

        return (
            <>
                {/* Summary cards */}
                <Row className="mb-3">
                    {[
                        { label: 'Total',    val: total,    color: '#4e73df', cls: 'mc-stat-total' },
                        { label: 'Online',   val: online,   color: '#2ecc71', cls: 'mc-stat-online' },
                        { label: 'Warning',  val: warning,  color: '#f6a623', cls: 'mc-stat-warning' },
                        { label: 'Critical', val: critical, color: '#e74a3b', cls: 'mc-stat-critical' },
                        { label: 'Offline',  val: offline,  color: '#aaa',    cls: 'mc-stat-offline' },
                    ].map(s => (
                        <Col key={s.label} xs={6} sm={4} md={2} className="mb-2">
                            <div className={`mc-stat-card ${s.cls}`}>
                                <span className="mc-stat-label">{s.label}</span>
                                <span className="mc-stat-val" style={{ color: s.color }}>{s.val}</span>
                            </div>
                        </Col>
                    ))}
                </Row>

                {/* Filter buttons */}
                <div className="mc-filter-bar">
                    {FILTERS.map(f => (
                        <button
                            key={f.key}
                            className={`mc-filter-btn ${activeFilter === f.key ? 'mc-filter-active' : ''}`}
                            onClick={() => this.setState({ activeFilter: f.key })}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                {/* Grouped cards */}
                {groups.length === 0 && <p className="text-muted">No servers match this filter.</p>}
                {groups.map(group => (
                    <div key={group.key} className="mc-group">
                        <div className="mc-group-header">
                            {group.label}
                            <span className="mc-group-count">{group.servers.length} servers</span>
                        </div>
                        <div className="mc-cards-grid">
                            {group.servers.map((server, idx) => this.renderCard(server, idx))}
                        </div>
                    </div>
                ))}
            </>
        );
    }

    /* ── Dialer Calls Tab ── */
    renderDialerCalls() {
        const { latestList, latestLoading } = this.state;

        if (latestLoading) return <p className="text-muted">Loading...</p>;
        if (!latestList || latestList.length === 0)
            return <p className="text-muted">No server data available.</p>;

        const dialers = latestList
            .filter(s => {
                const n = (s.serverName || '').toUpperCase();
                return n.includes('_AHM') || n.includes('_BLR');
            })
            .map(s => ({ ...s, _group: getGroup(s.serverName) }))
            .sort((a, b) => (a.serverName || '').localeCompare(b.serverName || '', undefined, { numeric: true, sensitivity: 'base' }));

        const ahmServers = dialers.filter(s => s._group === 'ahm');
        const blrServers = dialers.filter(s => s._group === 'blr');

        const totalAhm = ahmServers.reduce((sum, s) => sum + (s.activeCalls || 0), 0);
        const totalBlr = blrServers.reduce((sum, s) => sum + (s.activeCalls || 0), 0);
        const grandTotal = totalAhm + totalBlr;

        const renderCallsGroup = (servers, label, total) => (
            <div className="mc-calls-group">
                <div className="mc-calls-group-header">
                    <span>{label}</span>
                    <span className="mc-calls-group-total">{total.toLocaleString()} total calls</span>
                </div>
                <div className="mc-calls-grid">
                    {servers.map((s, i) => (
                        <div key={i} className={`mc-calls-card ${s.activeCalls > 0 ? 'mc-calls-card-active' : ''}`}>
                            <div className="mc-calls-card-info">
                                <div className="mc-calls-card-name">{s.serverName}</div>
                                <div className="mc-calls-card-ip">{s.ip}</div>
                            </div>
                            <div className="mc-calls-card-right">
                                <div className="mc-calls-card-count">{(s.activeCalls || 0).toLocaleString()}</div>
                                <div className="mc-calls-card-label">calls</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );

        return (
            <>
                {/* Grand total banner */}
                <div className="mc-calls-total-banner">
                    <span className="mc-calls-total-label">Total Active Calls</span>
                    <span className="mc-calls-total-val">{grandTotal.toLocaleString()}</span>
                    <span className="mc-calls-total-split">AHM: {totalAhm.toLocaleString()} &nbsp;|&nbsp; BLR: {totalBlr.toLocaleString()}</span>
                </div>

                {renderCallsGroup(ahmServers, 'AHM Dialers', totalAhm)}
                {renderCallsGroup(blrServers, 'BLR Dialers', totalBlr)}
            </>
        );
    }

    /* ── History Tab ── */
    renderHistory() {
        const { historyServerName, historyFromDate, historyToDate, historyList, historyLoading, serverNameList } = this.state;

        const columns = [
            { dataField: 'reqDate',     text: 'Date',           sort: true },
            { dataField: 'status',      text: 'Status',         sort: true },
            { dataField: 'cpuUsed',     text: 'CPU %',          sort: true },
            { dataField: 'memUsed',     text: 'Mem Used (GB)',   sort: true },
            { dataField: 'memFree',     text: 'Mem Free (GB)',   sort: true },
            { dataField: 'diskUsed',    text: 'Disk Used (GB)',  sort: true },
            { dataField: 'diskFree',    text: 'Disk Free (GB)',  sort: true },
            { dataField: 'load1m',      text: 'Load 1m',        sort: true },
            { dataField: 'load5m',      text: 'Load 5m',        sort: true },
            { dataField: 'load15m',     text: 'Load 15m',       sort: true },
            { dataField: 'activeCalls', text: 'Active Calls',   sort: true },
        ];

        const chartData = [...historyList].reverse().map(r => ({
            time:        r.reqDate ? r.reqDate.slice(11, 16) : '',
            cpu:         r.cpuUsed,
            memUsed:     r.memUsed,
            activeCalls: r.activeCalls,
            load1m:      r.load1m,
        }));

        return (
            <div>
                <Row className="mb-3 align-items-end">
                    <Col xs={12} md={3}>
                        <Form.Label className="mc-label">Server</Form.Label>
                        <Form.Control as="select" value={historyServerName}
                            onChange={e => this.setState({ historyServerName: e.target.value })}>
                            {serverNameList.map((s, i) => <option key={i} value={s}>{s}</option>)}
                        </Form.Control>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Label className="mc-label">From</Form.Label><br />
                        <DatePicker selected={historyFromDate}
                            onChange={date => this.setState({ historyFromDate: date })}
                            showTimeSelect dateFormat="yyyy-MM-dd HH:mm" className="form-control" />
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Label className="mc-label">To</Form.Label><br />
                        <DatePicker selected={historyToDate}
                            onChange={date => this.setState({ historyToDate: date })}
                            showTimeSelect dateFormat="yyyy-MM-dd HH:mm" className="form-control" />
                    </Col>
                    <Col xs={12} md={2}>
                        <Button variant="primary" onClick={() => this.loadHistory()} disabled={historyLoading}>
                            {historyLoading ? 'Loading...' : 'Search'}
                        </Button>
                    </Col>
                </Row>

                {historyList.length > 0 && (
                    <>
                        <div className="mc-chart-wrap mb-4">
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="time" />
                                    <YAxis yAxisId="left" />
                                    <YAxis yAxisId="right" orientation="right" />
                                    <Tooltip />
                                    <Legend />
                                    <Line yAxisId="left"  type="monotone" dataKey="cpu"         stroke="#4e73df" name="CPU %"          dot={false} />
                                    <Line yAxisId="left"  type="monotone" dataKey="memUsed"      stroke="#2ecc71" name="Mem Used (GB)"  dot={false} />
                                    <Line yAxisId="right" type="monotone" dataKey="activeCalls"  stroke="#f6a623" name="Active Calls"   dot={false} />
                                    <Line yAxisId="left"  type="monotone" dataKey="load1m"       stroke="#e74a3b" name="Load 1m"        dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <BootstrapTable keyField="id" data={historyList} columns={columns}
                            pagination={paginationFactory({ sizePerPage: 25 })}
                            striped hover condensed noDataIndication="No data" />
                    </>
                )}
                {!historyLoading && historyList.length === 0 &&
                    <p className="text-muted">Select a server and date range, then click Search.</p>}
            </div>
        );
    }

    /* ── All Servers Range Tab ── */
    renderAllServers() {
        const { allFromDate, allToDate, allList, allLoading } = this.state;

        const columns = [
            { dataField: 'serverName',  text: 'Server',         sort: true },
            { dataField: 'ip',          text: 'IP',             sort: true },
            { dataField: 'reqDate',     text: 'Date',           sort: true },
            { dataField: 'status',      text: 'Status',         sort: true,
              formatter: val => (
                  <span style={{ color: val === 'online' ? '#2ecc71' : '#e74a3b', fontWeight: 600 }}>● {val}</span>
              )},
            { dataField: 'cpuUsed',     text: 'CPU %',          sort: true },
            { dataField: 'memUsed',     text: 'Mem Used (GB)',   sort: true },
            { dataField: 'memTotal',    text: 'Mem Total (GB)',  sort: true },
            { dataField: 'diskUsed',    text: 'Disk Used (GB)',  sort: true },
            { dataField: 'diskTotal',   text: 'Disk Total (GB)', sort: true },
            { dataField: 'load1m',      text: 'Load 1m',        sort: true },
            { dataField: 'activeCalls', text: 'Active Calls',   sort: true },
        ];

        return (
            <div>
                <Row className="mb-3 align-items-end">
                    <Col xs={12} md={3}>
                        <Form.Label className="mc-label">From</Form.Label><br />
                        <DatePicker selected={allFromDate}
                            onChange={date => this.setState({ allFromDate: date })}
                            showTimeSelect dateFormat="yyyy-MM-dd HH:mm" className="form-control" />
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Label className="mc-label">To</Form.Label><br />
                        <DatePicker selected={allToDate}
                            onChange={date => this.setState({ allToDate: date })}
                            showTimeSelect dateFormat="yyyy-MM-dd HH:mm" className="form-control" />
                    </Col>
                    <Col xs={12} md={2}>
                        <Button variant="primary" onClick={() => this.loadAllServers()} disabled={allLoading}>
                            {allLoading ? 'Loading...' : 'Search'}
                        </Button>
                    </Col>
                </Row>

                {allList.length > 0 ? (
                    <ToolkitProvider keyField="id" data={allList} columns={columns} search>
                        {props => (
                            <>
                                <SearchBar {...props.searchProps} />
                                <BootstrapTable {...props.baseProps}
                                    pagination={paginationFactory({ sizePerPage: 25 })}
                                    striped hover condensed noDataIndication="No data" />
                            </>
                        )}
                    </ToolkitProvider>
                ) : (
                    !allLoading && <p className="text-muted">Select a date range and click Search.</p>
                )}
            </div>
        );
    }

    render() {
        const { activeTab, latestLoading, lastUpdated } = this.state;
        const p = n => String(n).padStart(2, '0');
        const updatedStr = lastUpdated
            ? `${p(lastUpdated.getHours())}:${p(lastUpdated.getMinutes())}:${p(lastUpdated.getSeconds())}`
            : '';
        return (
            <div className="animated fadeIn">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h4 className="mc-page-title">Server Monitoring</h4>
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                        {updatedStr && (
                            <span className="mc-last-updated">
                                <i className="fa fa-clock-o mr-1"></i> {updatedStr}
                            </span>
                        )}
                        <Button variant="primary" size="sm" onClick={() => this.loadLatest()} disabled={latestLoading}>
                            <i className={`fa fa-refresh mr-1 ${latestLoading ? 'fa-spin' : ''}`}></i>
                            {latestLoading ? 'Refreshing...' : 'Refresh'}
                        </Button>
                    </div>
                </div>

                <Tab.Container activeKey={activeTab} onSelect={k => this.setState({ activeTab: k })}>
                    <div className="mc-tab-container">
                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item>
                                <Nav.Link eventKey="latest"><i className="fa fa-server mr-1"></i> Latest Status</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="calls"><i className="fa fa-phone mr-1"></i> Dialer Calls</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="history"><i className="fa fa-line-chart mr-1"></i> Server History</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="all"><i className="fa fa-table mr-1"></i> All Servers Range</Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey="latest"  mountOnEnter unmountOnExit>{this.renderLatestCards()}</Tab.Pane>
                            <Tab.Pane eventKey="calls"   mountOnEnter unmountOnExit>{this.renderDialerCalls()}</Tab.Pane>
                            <Tab.Pane eventKey="history" mountOnEnter unmountOnExit>{this.renderHistory()}</Tab.Pane>
                            <Tab.Pane eventKey="all"     mountOnEnter unmountOnExit>{this.renderAllServers()}</Tab.Pane>
                        </Tab.Content>
                    </div>
                </Tab.Container>
            </div>
        );
    }
}

export default Monitoring;
